import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, MapPin } from 'lucide-react';
import { luxuryEase } from '../lib/motion';
import { projects } from '../data/siteData';
import Photo from './Photo';

const heroImage = projects[0].coverImage;

export default function Hero({ introDone, onExploreClick, onContactClick, onScrollDown }) {
  const [cinematic, setCinematic] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px) and (prefers-reduced-motion: no-preference)');
    const apply = () => setCinematic(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
  return (
    <section
      id="home"
      className="relative min-h-[100svh] flex items-end overflow-hidden bg-nosta-ink"
    >
      <motion.div
        className="absolute inset-0 z-0 will-change-transform"
        initial={{ scale: 1 }}
        animate={introDone && cinematic ? { scale: 1.08 } : { scale: 1 }}
        transition={
          introDone && cinematic
            ? { duration: 28, ease: 'linear', repeat: Infinity, repeatType: 'reverse' }
            : { duration: 0 }
        }
      >
        <Photo
          src={heroImage}
          alt={projects[0].coverImageAlt}
          priority
          sizes="100vw"
          className="h-full w-full object-[68%_center] sm:object-[center_42%] lg:object-center"
        />
      </motion.div>
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-black/80 via-black/50 to-black/25" />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-black/80 via-transparent to-black/35" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 pt-[calc(5.5rem+env(safe-area-inset-top))] sm:pt-32 lg:pt-36 pb-[calc(5.5rem+env(safe-area-inset-bottom))] sm:pb-28">
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={introDone ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
          transition={{ duration: 1, delay: 0.2, ease: luxuryEase }}
          className="eyebrow text-nosta-goldSoft mb-4 sm:mb-6"
        >
          Nosta Yapı · Enez & İstanbul
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 36 }}
          animate={introDone ? { opacity: 1, y: 0 } : { opacity: 0, y: 36 }}
          transition={{ duration: 1.15, delay: 0.32, ease: luxuryEase }}
          className="font-display text-white text-[2.15rem] leading-[1.05] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.25rem] xl:leading-[0.95] max-w-4xl text-balance"
        >
          Enez’de hayalinizdeki
          <span className="italic text-nosta-goldSoft"> yaşam </span>
          yükseliyor.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={introDone ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 1, delay: 0.5, ease: luxuryEase }}
          className="mt-5 sm:mt-7 text-white/75 text-[0.95rem] sm:text-lg md:text-xl max-w-xl leading-relaxed font-light"
        >
          Denize yakın, doğayla iç içe 36 özel villa. Mühendislik disiplini ve mimari dil aynı çatı altında.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={introDone ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.95, delay: 0.68, ease: luxuryEase }}
          className="mt-8 sm:mt-10 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 sm:gap-4"
        >
          <button onClick={onExploreClick} className="gold-btn w-full sm:w-auto">
            Projeyi Keşfet
          </button>
          <button onClick={onContactClick} className="ghost-btn w-full sm:w-auto">
            Randevu Alın
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={introDone ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 1.1, duration: 1.1, ease: luxuryEase }}
          className="mt-10 sm:mt-16 flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-5 sm:gap-6 border-t border-white/15 pt-5 sm:pt-6"
        >
          <div className="flex items-center gap-2 text-white/70 text-sm">
            <MapPin size={15} className="text-nosta-goldSoft shrink-0" />
            Enez, Edirne · Satışta
          </div>
          <div className="grid grid-cols-3 w-full sm:w-auto sm:min-w-[20rem] gap-3 sm:gap-8 text-white text-center">
            <div className="flex flex-col items-center">
              <div className="font-display text-2xl sm:text-3xl leading-none whitespace-nowrap inline-flex flex-row items-baseline justify-center">
                <span>3</span>
                <span>6</span>
              </div>
              <div className="text-[10px] sm:text-[11px] tracking-wideish uppercase text-white/50 mt-2 whitespace-nowrap">
                Özel villa
              </div>
            </div>
            <div className="flex flex-col items-center">
              <div className="font-display text-2xl sm:text-3xl leading-none tabular-nums">2</div>
              <div className="text-[10px] sm:text-[11px] tracking-wideish uppercase text-white/50 mt-2 whitespace-nowrap">
                Şehir
              </div>
            </div>
            <div className="flex flex-col items-center">
              <div className="font-display text-2xl sm:text-3xl leading-none">A+</div>
              <div className="text-[10px] sm:text-[11px] tracking-wideish uppercase text-white/50 mt-2 whitespace-nowrap">
                Mühendislik
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <button
        onClick={onScrollDown}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50 hover:text-white transition-colors hidden lg:flex flex-col items-center gap-3"
        aria-label="Aşağı kaydır"
      >
        <span className="text-[10px] tracking-brand uppercase">Kaydır</span>
        <span className="scroll-pulse block h-8 w-px bg-white/50" />
        <ArrowDown size={14} />
      </button>
    </section>
  );
}
